import { useEffect } from 'react';

interface KeyboardShortcutsProps {
  onFocusToggle: () => void;
  onExport: () => void;
  onHistory: () => void;
  onEvaluate?: () => void;
  onTriggerOrAcceptAutocomplete?: () => void; // Ctrl+B to trigger or accept autocomplete
  onRejectSuggestion?: () => boolean; // ESC to reject suggestion
  onToggleSnippets?: () => void; // Ctrl+Shift+S to toggle snippets modal
  onSaveSnippet?: () => void; // Ctrl+W to save current content as snippet
}

export const useKeyboardShortcuts = ({
  onFocusToggle,
  onExport,
  onHistory,
  onEvaluate,
  onTriggerOrAcceptAutocomplete,
  onRejectSuggestion,
  onToggleSnippets,
  onSaveSnippet
}: KeyboardShortcutsProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Handle Escape key to reject suggestions (without modifier)
      if (event.key === 'Escape' && onRejectSuggestion) {
        const handled = onRejectSuggestion();
        if (handled) {
          event.preventDefault();
          return;
        }
      }
      
      // Check for Cmd/Ctrl key for other shortcuts
      const isModified = event.metaKey || event.ctrlKey;
      
      if (!isModified) return;

      switch (event.key.toLowerCase()) {
        case 'f':
          event.preventDefault();
          onFocusToggle();
          break;
        case 'e':
          event.preventDefault();
          onExport();
          break;
        case 'h':
          event.preventDefault();
          onHistory();
          break;
        case 'r':
          if (onEvaluate) {
            event.preventDefault();
            onEvaluate();
          }
          break;
        case 'b':
          if (onTriggerOrAcceptAutocomplete) {
            event.preventDefault();
            onTriggerOrAcceptAutocomplete();
          }
          break;
        case 's':
          if (event.shiftKey && onToggleSnippets) {
            event.preventDefault();
            onToggleSnippets();
          }
          break;
        case 'l':
          if (event.altKey && onSaveSnippet) {
            event.preventDefault();
            onSaveSnippet();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onFocusToggle, onExport, onHistory, onEvaluate, onTriggerOrAcceptAutocomplete, onRejectSuggestion, onToggleSnippets, onSaveSnippet]);
};